import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../common/Spinner';
import { ArtifactoryMarkdown } from '../artifactory/ArtifactoryMarkdown';
import { FiCopy, FiMaximize, FiMinimize, FiEye, FiEdit, FiCheck, FiX } from 'react-icons/fi';
import EditableList from '../common/EditableList';

// Define font size options
type FontSizeOption = 'small' | 'medium' | 'large';

const SourcePanel: React.FC = () => {
  const { darkMode } = useTheme();
  const { state, isLoading, sendSourceMessage, updateSourceData, setPendingMapFocus, setActivePanel } = useFabricationStore();

  const handleShowInMap = (mapId: string) => {
    setActivePanel(5);
    // Small delay to let the panel mount before flying
    setTimeout(() => setPendingMapFocus(mapId), 100);
  };
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [lineCountDelta, setLineCountDelta] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState(true);

  // Compact/expanded toggle for all three lists
  const [isCompact, setIsCompact] = useState<boolean>(() =>
    localStorage.getItem('sourcePanelCompact') === 'true'
  );
  useEffect(() => {
    localStorage.setItem('sourcePanelCompact', isCompact.toString());
  }, [isCompact]);

  // Add font size state with medium as default
  const [fontSize, setFontSize] = useState<FontSizeOption>(() => {
    // Try to get from localStorage, default to medium if not found
    const savedSize = localStorage.getItem('sourcePanelFontSize');
    return (savedSize as FontSizeOption) || 'medium';
  });
  
  // Save font size preference to localStorage
  useEffect(() => {
    localStorage.setItem('sourcePanelFontSize', fontSize);
  }, [fontSize]);
  
  // Get font size classes based on selected option
  const getFontSizeClasses = (size: FontSizeOption) => {
    switch (size) {
      case 'small':
        return 'text-xs prose-xs';
      case 'medium':
        return 'text-sm prose-sm';
      case 'large':
        return 'text-base prose-base';
      default:
        return 'text-sm prose-sm';
    }
  };
  
  // Copy character bible to clipboard
  const copyCharacterBible = () => {
    if (!state?.character_bible) return;
    
    navigator.clipboard.writeText(state.character_bible)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isLoading) {
      const prevLineCount = state?.character_bible?.split('\n').length ?? 0;
      await sendSourceMessage(message);
      setMessage('');
      const newLineCount = useFabricationStore.getState().state?.character_bible?.split('\n').length ?? 0;
      setLineCountDelta(newLineCount - prevLineCount);
    }
  };
  
  // Store the original bible content when entering edit mode
  const [originalBible, setOriginalBible] = useState<string | null>(null);
  // Store the current edit value
  const [bibleEditValue, setBibleEditValue] = useState<string>('');
  
  // Set up debounced update function
  const debouncedUpdateBible = useCallback(
    debounce((value: string) => {
      updateSourceData({ character_bible: value });
    }, 500),
    [updateSourceData]
  );
  
  // Handle character bible updates with debounce
  const handleBibleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setBibleEditValue(newValue);
    debouncedUpdateBible(newValue);
  };
  
  // Handle list updates
  const handleTethicsChange = (items: string[]) => {
    updateSourceData({ tethics: items });
  };
  
  const handlePatternsChange = (items: string[]) => {
    updateSourceData({ patterns: items });
  };
  
  const handleAntipatternsChange = (items: string[]) => {
    updateSourceData({ antipatterns: items });
  };
  
  const getPlaceholder = () => {
    if (!state?.conversation_source?.length)
      return "Describe your persona's purpose and personality...";
    if ((state?.character_bible?.length ?? 0) < 200)
      return "Add more detail about how they think and communicate...";
    return "Refine a specific aspect, add a pattern, strengthen a trait...";
  };

  if (!state) return null;
  
  const isSourceLocked = state.stage_locked && state.stage !== 0;
  
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Source</h2>
        
        {/* Font size selector */}
        <div className="flex items-center space-x-2">
          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Size:</span>
          <div className="flex border rounded overflow-hidden">
            <button
              onClick={() => setFontSize('small')}
              className={`px-2 py-1 ${
                fontSize === 'small' 
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                  : darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}
              title="Small text"
            >
              <FiMinimize size={14} />
            </button>
            <button
              onClick={() => setFontSize('medium')}
              className={`px-2 py-1 ${
                fontSize === 'medium' 
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                  : darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}
              title="Medium text"
            >
              A
            </button>
            <button
              onClick={() => setFontSize('large')}
              className={`px-2 py-1 ${
                fontSize === 'large' 
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                  : darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}
              title="Large text"
            >
              <FiMaximize size={14} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className={`font-medium ${fontSize === 'small' ? 'text-sm' : fontSize === 'medium' ? 'text-base' : 'text-lg'}`}>
            Character Bible
          </h3>
          
          <div className="flex items-center space-x-2">
            {/* Buttons for edit/preview mode */}
            <div className="flex space-x-2">
              {!previewMode && (
                <button
                  onClick={() => {
                    // Cancel edit and return to preview without saving changes
                    setPreviewMode(true);
                    // Reset to original content if we have it
                    if (originalBible !== null) {
                      updateSourceData({ character_bible: originalBible });
                      setBibleEditValue(originalBible);
                    }
                  }}
                  className={`px-2 py-1 rounded-md flex items-center ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                  title="Cancel editing and return to preview"
                >
                  <FiX className="mr-1" />
                  <span className="text-xs font-medium">Cancel</span>
                </button>
              )}
              
              <button
                onClick={() => {
                  if (previewMode) {
                    // Switch to edit mode
                    setPreviewMode(false);
                    // Store the original bible content for potential cancellation
                    setOriginalBible(state.character_bible || '');
                    // Initialize the edit value
                    setBibleEditValue(state.character_bible || '');
                  } else {
                    // Save changes and switch to preview mode
                    setPreviewMode(true);
                    // Force an immediate update instead of waiting for debounce
                    updateSourceData({ character_bible: bibleEditValue });
                  }
                }}
                className={`px-2 py-1 rounded-md flex items-center ${
                  previewMode 
                    ? darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-700' 
                    : darkMode ? 'bg-yellow-600 hover:bg-yellow-500 text-white' : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700'
                }`}
                title={previewMode ? "Switch to edit mode" : "Save changes and return to preview"}
              >
                {previewMode ? <FiEdit className="mr-1" /> : <FiCheck className="mr-1" />}
                <span className="text-xs font-medium">{previewMode ? "Edit" : "Save"}</span>
              </button>
            </div>
            
            {/* Copy button */}
            <button
              onClick={copyCharacterBible}
              className={`p-1 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
              title="Copy to clipboard"
            >
              <FiCopy className={copied ? 'text-green-500' : ''} />
              {copied && <span className="ml-1 text-xs text-green-500">Copied!</span>}
            </button>
          </div>
        </div>
        
        <div className={`rounded-lg ${
          darkMode
            ? 'bg-gray-700 ring-1 ring-gray-700/50'
            : 'bg-gray-100 ring-1 ring-gray-200'
        }`}>
          {previewMode ? (
            // Preview mode - show markdown
            <div className={`p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
              {state.character_bible ? (
                <ArtifactoryMarkdown content={state.character_bible} size={fontSize} />
              ) : (
                <span className={`italic ${darkMode ? 'text-gray-500' : 'text-gray-400'} ${getFontSizeClasses(fontSize)}`}>
                  No character bible defined yet.
                </span>
              )}
            </div>
          ) : (
            // Edit mode - show textarea
            <div className="relative">
              <textarea
                className={`min-h-[200px] w-full p-4 font-mono ${getFontSizeClasses(fontSize)} resize-y ${
                  darkMode 
                    ? 'bg-gray-800 text-white border-gray-700' 
                    : 'bg-white text-gray-800 border-gray-200'
                } border rounded-md`}
                value={bibleEditValue || state.character_bible || ''}
                onChange={handleBibleChange}
                placeholder="Enter character bible..."
                disabled={isSourceLocked}
                autoFocus
              />
            </div>
          )}
        </div>
      </div>
      
      {/* ── Identity Pillars section ─────────────────────────────────────── */}
      <div className="mb-4">
        {/* Section header row */}
        <div className="flex items-center justify-between mb-3">
          <span className={`font-semibold text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Identity Pillars
          </span>
          <button
            onClick={() => setIsCompact(c => !c)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              darkMode
                ? 'border-gray-600 text-gray-300 hover:border-gray-400 bg-gray-700/50'
                : 'border-gray-300 text-gray-600 hover:border-gray-500 bg-gray-50'
            }`}
          >
            {isCompact ? 'Expanded ▴' : 'Compact ▾'}
          </button>
        </div>

        {isCompact ? (
          /* ── COMPACT MODE: chip strips ─────────────────────────────────── */
          <div className="flex flex-col gap-2">
            {/* Tethics */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`flex items-center gap-1 font-semibold flex-shrink-0 border-l-4 border-red-500 pl-2 ${getFontSizeClasses(fontSize).split(' ')[0]} ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                ⚡ Tethics ({(state.tethics || []).length})
              </span>
              {(state.tethics || []).slice(0, 5).map((item, i) => (
                <span
                  key={i}
                  className={`px-2 py-0.5 rounded-full border ${getFontSizeClasses(fontSize).split(' ')[0]} ${
                    darkMode
                      ? 'bg-red-900/30 text-red-300 border-red-700'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }`}
                >
                  {item.length > 30 ? item.slice(0, 30) + '…' : item}
                </span>
              ))}
              {(state.tethics || []).length > 5 && (
                <span
                  className={`px-2 py-0.5 rounded-full text-xs border cursor-default ${
                    darkMode
                      ? 'bg-red-900/30 text-red-300 border-red-700'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }`}
                  title={(state.tethics || []).slice(5).join('\n')}
                >
                  +{(state.tethics || []).length - 5} more
                </span>
              )}
            </div>

            {/* Patterns */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`flex items-center gap-1 font-semibold flex-shrink-0 border-l-4 border-green-500 pl-2 ${getFontSizeClasses(fontSize).split(' ')[0]} ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                ✦ Patterns ({(state.patterns || []).length})
              </span>
              {(state.patterns || []).slice(0, 5).map((item, i) => (
                <span
                  key={i}
                  className={`px-2 py-0.5 rounded-full border ${getFontSizeClasses(fontSize).split(' ')[0]} ${
                    darkMode
                      ? 'bg-green-900/30 text-green-300 border-green-700'
                      : 'bg-green-50 text-green-700 border-green-200'
                  }`}
                >
                  {item.length > 30 ? item.slice(0, 30) + '…' : item}
                </span>
              ))}
              {(state.patterns || []).length > 5 && (
                <span
                  className={`px-2 py-0.5 rounded-full text-xs border cursor-default ${
                    darkMode
                      ? 'bg-green-900/30 text-green-300 border-green-700'
                      : 'bg-green-50 text-green-700 border-green-200'
                  }`}
                  title={(state.patterns || []).slice(5).join('\n')}
                >
                  +{(state.patterns || []).length - 5} more
                </span>
              )}
            </div>

            {/* Antipatterns */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`flex items-center gap-1 font-semibold flex-shrink-0 border-l-4 border-orange-500 pl-2 ${getFontSizeClasses(fontSize).split(' ')[0]} ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                ⊘ Antipatterns ({(state.antipatterns || []).length})
              </span>
              {(state.antipatterns || []).slice(0, 5).map((item, i) => (
                <span
                  key={i}
                  className={`px-2 py-0.5 rounded-full border ${getFontSizeClasses(fontSize).split(' ')[0]} ${
                    darkMode
                      ? 'bg-orange-900/30 text-orange-300 border-orange-700'
                      : 'bg-orange-50 text-orange-700 border-orange-200'
                  }`}
                >
                  {item.length > 30 ? item.slice(0, 30) + '…' : item}
                </span>
              ))}
              {(state.antipatterns || []).length > 5 && (
                <span
                  className={`px-2 py-0.5 rounded-full text-xs border cursor-default ${
                    darkMode
                      ? 'bg-orange-900/30 text-orange-300 border-orange-700'
                      : 'bg-orange-50 text-orange-700 border-orange-200'
                  }`}
                  title={(state.antipatterns || []).slice(5).join('\n')}
                >
                  +{(state.antipatterns || []).length - 5} more
                </span>
              )}
            </div>
          </div>
        ) : (
          /* ── EXPANDED MODE: full EditableList grid ─────────────────────── */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`border-l-4 border-red-500 pl-2`}>
              <div className={`flex items-center gap-1 text-xs font-semibold mb-1 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                ⚡ Tethics
              </div>
              <EditableList
                title="Technical Ethics"
                items={state.tethics || []}
                onChange={handleTethicsChange}
                placeholder="Add a technical ethics guideline..."
                disabled={isSourceLocked}
                fontSize={fontSize}
                mapIdPrefix="tethic"
                onShowInMap={handleShowInMap}
              />
            </div>

            <div className={`border-l-4 border-green-500 pl-2`}>
              <div className={`flex items-center gap-1 text-xs font-semibold mb-1 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                ✦ Patterns
              </div>
              <EditableList
                title="Patterns"
                items={state.patterns || []}
                onChange={handlePatternsChange}
                placeholder="Add a pattern..."
                disabled={isSourceLocked}
                fontSize={fontSize}
                mapIdPrefix="pattern"
                onShowInMap={handleShowInMap}
              />
            </div>

            <div className={`border-l-4 border-orange-500 pl-2`}>
              <div className={`flex items-center gap-1 text-xs font-semibold mb-1 ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                ⊘ Antipatterns
              </div>
              <EditableList
                title="Antipatterns"
                items={state.antipatterns || []}
                onChange={handleAntipatternsChange}
                placeholder="Add an antipattern..."
                disabled={isSourceLocked}
                fontSize={fontSize}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* ── Mutate Character Bible section ───────────────────────────────── */}
      <div className="mb-4">
        {/* Section header */}
        <div className={`flex items-center gap-2 mb-3 border-l-4 border-indigo-500 pl-2 py-1 rounded-r-md ${
          darkMode ? 'bg-indigo-900/10' : 'bg-indigo-50'
        }`}>
          <h3 className={`font-semibold ${getFontSizeClasses(fontSize).split(' ')[0]} ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
            ✏ Mutate Character Bible
          </h3>
        </div>

        {/* History box — fixed height, scrollable.
            conversation_source can be undefined on freshly-loaded
            personas whose backend record predates the field; guard
            both .length and .map. */}
        <div className={`h-[200px] overflow-y-auto rounded-md p-3 mb-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          {(state.conversation_source?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-2">
              {(state.conversation_source ?? []).map((msg, index) => (
                <div
                  key={msg.id || index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-3 py-2 ${getFontSizeClasses(fontSize).split(' ')[0]} ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-2xl rounded-tr-sm ml-auto'
                        : darkMode
                          ? 'bg-gray-600 text-gray-100 rounded-2xl rounded-tl-sm'
                          : 'bg-gray-200 text-gray-800 rounded-2xl rounded-tl-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <ArtifactoryMarkdown content={msg.content} size="small" />
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`h-full flex items-center justify-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No conversation yet.
            </div>
          )}
        </div>

        {/* Line count delta badges — above input bar */}
        {lineCountDelta !== null && lineCountDelta !== 0 && (
          <div className="mb-2 flex gap-2">
            {lineCountDelta > 0 && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
                +{lineCountDelta} lines
              </span>
            )}
            {lineCountDelta < 0 && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
                {lineCountDelta} lines
              </span>
            )}
          </div>
        )}

        {/* Input bar — always visible, never inside scroll container */}
        <form onSubmit={handleSubmit}>
          <div className={`flex border-t pt-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <input
              type="text"
              className={`flex-grow p-2 rounded-l-md ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
              }`}
              placeholder={getPlaceholder()}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading || (state.stage_locked && state.stage !== 0)}
            />
            <button
              type="submit"
              className={`px-4 py-2 rounded-r-md ${
                darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
              } text-white ${(isLoading || (state.stage_locked && state.stage !== 0)) ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isLoading || !message.trim() || (state.stage_locked && state.stage !== 0)}
            >
              {isLoading ? <Spinner size="sm" /> : 'Send'}
            </button>
          </div>
          <p className={`${getFontSizeClasses(fontSize).split(' ')[0]} text-gray-500 mt-1`}>
            Tip: For best results, be specific about what to add, modify, or remove from the persona.
          </p>
        </form>
      </div>
    </div>
  );
};

export default SourcePanel;
